import React, { useEffect, useState } from 'react';
import TeamEditor from './TeamEditor';
import styles from './teamManager.module.css';
import { showErrorToast, showInfoToast } from '../../../../code/ToastHelper';
import { usePermissionGuard } from '../../adminPanelTools';
export default function TeamManager() {
    const checking = usePermissionGuard('handle_teams');
    const [categories, setCategories] = useState([]);
    const [teams, setTeams] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showEditorModal, setShowEditorModal] = useState(false);
    const [editTeam, setEditTeam] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchTeams();
    }, [selectedCategory, searchTerm]);

    async function fetchCategories() {
        try {
            const res = await fetch('/api/team/categories');
            if (!res.ok) throw new Error('Failed to fetch categories');
            const data = await res.json();
            setCategories(data);
            const defaultCategory = data.find(cat => cat.name === 'Fußball');
            if (defaultCategory) {
                setSelectedCategory(`${defaultCategory.id}`);
            }
        } catch {
            showErrorToast('Fehler beim Laden der Kategorien');
        }
    }

    function parseTeamAge(name) {
        // Match something like U19, U17, U9.2, U5/U6
        const match = name.match(/U\s*(\d+)/i);
        if (match) {
            return parseInt(match[1], 10);
        }

        // Handle special case: "Bambini" → treat as youngest (e.g., U5)
        if (/bambini/i.test(name)) {
            return 5;
        }

        return null; // no numeric age found
    }

    function sortTeams(teams) {
        const footballCatOrder = {
            Senioren: 0,
            Junioren: 1,
            Sonstige: 2,
        };

        return [...teams].sort((a, b) => {
            // 1. Sort by football_cat
            const catA = footballCatOrder[a.football_cat] ?? 99;
            const catB = footballCatOrder[b.football_cat] ?? 99;
            if (catA !== catB) return catA - catB;

            // 2. If Junioren, sort by age group (high → low)
            if (a.football_cat === 'Junioren') {
                const ageA = parseTeamAge(a.name);
                const ageB = parseTeamAge(b.name);

                if (ageA !== null && ageB !== null) {
                    return ageB - ageA; // higher age first
                } else if (ageA !== null) {
                    return -1;
                } else if (ageB !== null) {
                    return 1;
                }
            }

            // 3. Fallback: alphabetical by name
            return a.name.localeCompare(b.name, 'de', { numeric: true });
        });
    }

    async function fetchTeams() {
        try {
            const params = new URLSearchParams({
                search: searchTerm,
            });
            if (selectedCategory) params.append('category_id', selectedCategory);

            const res = await fetch(`/api/team?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to fetch teams');
            const data = await res.json();

            setTeams(data.teams || data);
        } catch {
            showErrorToast('Fehler beim Laden der Teams');
        }
    }

    const handleDelete = async (team) => {
        if (!window.confirm(`Möchtest du das Team "${team.name}" wirklich löschen?`)) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/team/${team.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Failed to delete team');
            showInfoToast(`Team "${team.name}" erfolgreich gelöscht.`);
            fetchTeams();
        } catch {
            showErrorToast('Fehler beim Löschen');
        }
    };

    const handleCreate = () => {
        setEditTeam(null);
        setShowEditorModal(true);
    };

    const handleEdit = (team) => {
        setEditTeam(team);
        setShowEditorModal(true);
    };

    const handleEditorClose = (refresh = false) => {
        setShowEditorModal(false);
        setEditTeam(null);
        if (refresh) fetchTeams();
    };

    if (checking) {
        return (<div></div>)
    }
    return (
        <div className={styles.container}>
            <h1>Teams verwalten</h1>

            <div className={styles.controls}>
                <select
                    value={selectedCategory}
                    onChange={e => setSelectedCategory(e.target.value)}
                    className={styles.categorySelect}
                >
                    <option value="">Alle Kategorien</option>
                    {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>

                <input
                    type="text"
                    placeholder="Nach Team suchen..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                />

                {(
                    selectedCategory === "1" ||
                    ((selectedCategory === "2" || selectedCategory === "3") && teams.length === 0)
                ) && (
                        <button onClick={handleCreate} className={styles.createButton}>
                            Neues Team hinzufügen
                        </button>
                    )}
            </div>
            <ul className={styles.teamList}>
                {teams.length === 0 && <li className={styles.noTeams}>Keine Teams gefunden.</li>}
                {sortTeams(teams).map(team => (
                    <li key={team.id} className={styles.teamCard}>
                        <div className={styles.teamHeader}>
                            <div>
                                <h2 className={styles.teamName}>{team.name}</h2>
                                <span className={styles.categoryBadge}>
                                    {categories.find(c => c.id === team.category_id)?.name?.toUpperCase() || 'KEINE KATEGORIE'}
                                </span>
                            </div>
                            <div className={styles.actions}>
                                <button onClick={() => handleEdit(team)} className={styles.editButton}>Bearbeiten</button>
                                <button onClick={() => handleDelete(team)} className={styles.deleteButton}>Löschen</button>
                            </div>
                        </div>

                        {team.appointments && team.appointments.length > 0 && (
                            <div className={styles.appointments}>
                                <strong>Termine:</strong>
                                <div className={styles.badgeContainer}>
                                    {team.appointments.map(app => {
                                        const start = app.start_time?.slice(0, 5);
                                        const end = app.end_time?.slice(0, 5);
                                        const location = app.location_bezeichnung
                                            ? `${app.location_bezeichnung}: ${app.location_name}`
                                            : app.location_name || '';

                                        if (app.recurrence_rule) {
                                            const dayMap = {
                                                MO: 'Montag',
                                                TU: 'Dienstag',
                                                WE: 'Mittwoch',
                                                TH: 'Donnerstag',
                                                FR: 'Freitag',
                                                SA: 'Samstag',
                                                SU: 'Sonntag',
                                            };

                                            const days = (app.recurrence_rule.match(/BYDAY=([A-Z,]+)/)?.[1] || '')
                                                .split(',')
                                                .map(code => dayMap[code])
                                                .filter(Boolean)
                                                .join(', ');

                                            return (
                                                <span key={app.id} className={styles.appointmentBadge}>
                                                    {days}, {start}–{end} Uhr {location && `• ${location}`}
                                                </span>
                                            );
                                        } else {
                                            const date = new Date(app.appointment_date);
                                            const formattedDate = date.toLocaleDateString('de-DE', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                            });

                                            return (
                                                <span key={app.id} className={styles.appointmentBadge}>
                                                    📅 {formattedDate}, {start}–{end} Uhr {location && `• ${location}`}
                                                </span>
                                            );
                                        }
                                    })}
                                </div>
                            </div>
                        )}

                        {team.assignments && team.assignments.length > 0 && (
                            <div className={styles.contacts}>
                                <strong>Kontakte:</strong>
                                <div className={styles.inlineContacts}>
                                    {team.assignments.map(assign => {
                                        const initials = assign.contact
                                            ? `${assign.contact.first_name?.[0] || ''}${assign.contact.family_name?.[0] || ''}`.toUpperCase()
                                            : '';
                                        return (
                                            <span key={assign.id} className={styles.contactBadge} title={`${assign.contact?.first_name} ${assign.contact?.family_name}${assign.position ? ` - ${assign.position}` : ''}`}>
                                                <span className={styles.avatar}>{initials}</span> {assign.contact?.first_name} {assign.contact?.family_name}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
            {showEditorModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <TeamEditor
                            initialData={editTeam}
                            categories={categories}
                            onClose={handleEditorClose}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
